import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { GetDataDTO } from "../../schema/Common/GetDataDTO";
import { CourseSubscriptionDTO } from "../../schema/UserCourse/CourseSubscriptionDTO";
import { CourseSubscriptionFilterDTO } from "../../schema/UserCourse/CourseSubscriptionFilterDTO";
import { UserCourseProgressionDTO } from "../../schema/UserCourse/UserCourseProgressionDTO";
import { UserCourseProgressionLessonDTO } from "../../schema/UserCourse/UserCourseProgressionLessonDTO";

@Injectable({
    providedIn: "root"
})
export class ApiCourseSubscriptionService {
    private relativePath: string;

    constructor(private httpClient: HttpClient) {
        this.relativePath = `${environment.userCourses.url}/api/v1/course-subscriptions`;
    }

    public GetList(filters: CourseSubscriptionFilterDTO): Observable<GetDataDTO<CourseSubscriptionDTO>> {
        let params = new HttpParams().set("skip", filters.skip.toString()).set("take", filters.take.toString());

        if (filters.userId) params = params.append("userId", filters.userId);
        if (filters.courseId) params = params.append("courseId", filters.courseId);
        if (filters.active !== undefined) params = params.append("active", filters.active.toString());
        if (filters.progressionId) params = params.append("progressionId", filters.progressionId);
        if (filters.orderBy) params = params.append("orderBy", filters.orderBy);
        if (filters.sortOrder) params = params.append("sortOrder", filters.sortOrder);
        if (filters.originType !== undefined) params = params.append("originType", filters.originType.toString());
        if (filters.count !== undefined) params = params.append("count", filters.count.toString());

        return this.httpClient.get<GetDataDTO<CourseSubscriptionDTO>>(this.relativePath, {
            params: params
        });
    }

    public GetProgressionByCourseSubscription(id: string): Observable<UserCourseProgressionDTO> {
        return this.httpClient.get<UserCourseProgressionDTO>(`${this.relativePath}/${id}/progression`);
    }

    public GetLessonsProgressionsByCourseSubscriptionId(id: string): Observable<UserCourseProgressionLessonDTO[]> {
        return this.httpClient.get<UserCourseProgressionLessonDTO[]>(`${this.relativePath}/${id}/progression/lessons`);
    }

    public GetLessonProgressionByCourseSubscriptionId(
        id: string,
        lessonId: string
    ): Observable<UserCourseProgressionLessonDTO> {
        return this.httpClient.get<UserCourseProgressionLessonDTO>(
            `${this.relativePath}/${id}/progression/lessons/${lessonId}`
        );
    }

    public CreateProgressionByCourseSubscriptionId(
        id: string,
        lessonId: string
    ): Observable<UserCourseProgressionLessonDTO> {
        return this.httpClient.post<UserCourseProgressionLessonDTO>(
            `${this.relativePath}/${id}/progression/lessons/${lessonId}`,
            null
        );
    }

    public SetProgressionByUserIdCourseIdLessonIdPositionInVideo(
        id: string,
        lessonId: string,
        positionInVideo: number
    ): Observable<any> {
        return this.httpClient.post(`${this.relativePath}/${id}/progression/lesson/${lessonId}/positionInVideo`, {
            positionInVideo: positionInVideo.toFixed()
        });
    }

    public SetProgressionByUserIdCourseIdLessonIdScormValues(
        id: string,
        lessonId: string,
        ScormValues: { [key: string]: string }
    ): Observable<any> {
        return this.httpClient.post(`${this.relativePath}/${id}/progression/lesson/${lessonId}/scorm-values`, {
            scormValues: ScormValues
        });
    }

    public SetProgressionByUserIdCourseIdLessonIdConclusion(
        id: string,
        lessonId: string,
        conclusion: boolean
    ): Observable<any> {
        return this.httpClient.post(`${this.relativePath}/${id}/progression/lesson/${lessonId}/conclusion`, {
            concluded: conclusion
        });
    }

    public SetCurrentLessonId(id: string, lessonId: string): Observable<UserCourseProgressionLessonDTO> {
        return this.httpClient.post<UserCourseProgressionLessonDTO>(
            `${this.relativePath}/${id}/progression/lesson/${lessonId}/current`,
            null
        );
    }

    public ResetProgression(id: string): Observable<any> {
        return this.httpClient.put(`${this.relativePath}/${id}/reset-progression`, null);
    }

    public GenerateCertificate(id: string, dateOfIssue?: string): Observable<any> {
        let params = new HttpParams();
        if (dateOfIssue) params = params.set("dateOfIssue", dateOfIssue);
        return this.httpClient.post(`${this.relativePath}/${id}/generate-certificate`, null, { params });
    }
}
